import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/content";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open
          ? "border-b border-line bg-night/90 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <nav
        aria-label="Navigation principale"
        className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 sm:px-8"
      >
        <Link to="/" data-testid="site-logo-link" aria-label="Chroma — retour à l'accueil">
          <img
            src="/assets/logo-white.png"
            alt="chroma."
            className="h-6 w-auto sm:h-7"
            width="109"
            height="27"
          />
        </Link>

        <ul className="hidden items-center gap-8 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink to={link.to} data-testid={link.testid}>
                {({ isActive }) => (
                  <span
                    className={`flex items-center gap-2.5 font-mono text-[11px] uppercase tracking-[0.25em] transition-colors duration-300 ${
                      isActive ? "text-glow" : "text-fog/70 hover:text-fog"
                    }`}
                  >
                    <span
                      className={`h-1.5 w-1.5 transition-colors duration-300 ${
                        isActive ? "bg-glow" : "bg-line group-hover:bg-ice"
                      }`}
                      aria-hidden="true"
                    />
                    {link.label}
                  </span>
                )}
              </NavLink>
            </li>
          ))}
        </ul>

        <button
          type="button"
          data-testid="nav-menu-button"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-11 w-11 items-center justify-center border border-line text-fog transition-colors duration-300 hover:border-glow hover:text-glow md:hidden"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 top-[61px] z-40 flex flex-col justify-center bg-night px-8 md:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div
              className="pointer-events-none absolute -right-24 top-10 h-72 w-72 rounded-full bg-aubergine/60 blur-[100px]"
              aria-hidden="true"
            />
            <ul className="relative space-y-7">
              {NAV_LINKS.map((link, i) => (
                <motion.li
                  key={link.to}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                  <NavLink to={link.to} data-testid={`mobile-${link.testid}`}>
                    {({ isActive }) => (
                      <span
                        className={`flex items-baseline gap-4 font-clash text-5xl font-semibold uppercase leading-none tracking-tight ${
                          isActive ? "text-glow" : "text-fog"
                        }`}
                      >
                        <span className="font-mono text-xs tracking-[0.3em] text-ice">
                          0{i + 1}
                        </span>
                        {link.label}
                      </span>
                    )}
                  </NavLink>
                </motion.li>
              ))}
            </ul>
            <motion.p
              className="relative mt-16 font-mono text-[11px] uppercase tracking-[0.3em] text-muted"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              Lyon — Underground latino
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
